import { useEffect, useMemo, useState } from 'react';
import {
    Box, Checkbox, FormControlLabel, FormHelperText, IconButton, Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { App } from 'BMapsSrc/BMapsApp';
import { DialogContentWithTooltip } from 'BMapsUI/common/DialogContentSection';
import { DialogPrimaryButton } from 'BMapsUI/common/DialogActionButtons';
import Select from 'BMapsUI/common/Select';
import BMapsTooltip from 'BMapsSrc/ui/BMapsTooltip';
import { wrapForDisabledTooltip } from 'BMapsSrc/ui/common/helpers';
import useCompoundUpdateSubscription from 'BMapsSrc/ui/common/helpers/useCompoundUpdateSubscription';
import { userConfirmation } from 'BMapsSrc/utils';
import { receptorGroupsOnChains } from 'BMapsSrc/integrations/fep';
import {
    MD_DEFAULTS, MD_DYNAMICS, MD_ENGINES, MD_LIGAND_CHARGES, MD_LIMITS, MD_OPTION_DEFAULTS,
    estimateGpuMinutes, mdOptionsProblem, mdParamsProblem, mdTargets, parseAnnealing,
    remdSpacingWarning, remdTemperatures, startMdSimulation,
} from 'BMapsSrc/integrations/md';
import { defaultTarget } from 'BMapsSrc/integrations/compute_targets';
import { BasicTextField } from '../../SimulationPane/Components';
import { ImportErrors } from '../../ImportPane/import_components';
import { CheckboxGroup, chainLabel, ligandLabel } from '../../FreeEnergyPane/Tabs/NewFreeEnergyJob';
import { openMdSidePanel } from '../MdSidePanel';

const DEFAULT_RUNTIME_HOURS = 24;

/** "About 35 min" / "about 2.5 h" */
export function describeGpuTime(minutes) {
    if (minutes < 90) return `about ${Math.max(1, Math.round(minutes))} min`;
    return `about ${(minutes / 60).toFixed(1)} h`;
}

/**
 * One line naming the options a job runs with, for the confirmation.
 * @param {object} options
 * @param {object} params
 * @returns {string}
 */
export function describeMdOptions(options, params) {
    const o = { ...MD_OPTION_DEFAULTS, ...(options || {}) };
    const parts = [MD_DYNAMICS.find((d) => d.key === o.dynamics)?.label || o.dynamics];
    if (Number(o.backboneRestraint) > 0) parts.push(`backbone restrained at ${o.backboneRestraint} kcal/mol/Å²`);
    if (o.neutralize === false) parts.push('not neutralized (no counter-ions)');
    else if (Number(o.saltMolar) > 0) parts.push(`${o.saltMolar} M NaCl`);
    if (o.annealing) {
        const { points } = parseAnnealing(o.annealingText);
        if (points) parts.push(`annealing ${points.map(([t, k]) => `${t} ps ${k} K`).join(' → ')}`);
    }
    if (o.replicaExchange) {
        parts.push(`replica exchange, ${o.replicas} replicas ${params.temperature_k}–${o.maxTemperatureK} K, swaps every ${o.exchangePs} ps`);
    }
    return parts.join('; ');
}

const PARAM_FIELDS = [
    { key: 'length_ns', helper: 'Per independent run' },
    { key: 'copies', helper: 'Runs of each compound that differ only in their random seed; they share one prepared starting structure' },
    { key: 'temperature_k', helper: '' },
    { key: 'frame_interval_ps', helper: 'Time between saved frames' },
    { key: 'skip_ps', helper: 'Heating at the start, left out of the analysis' },
];

export default function NewMdJob({
    initData, selected, setHighlightedJobId,
}) {
    const workspaceCompounds = useCompoundUpdateSubscription();
    const selectedCompounds = useMemo(() => initData?.selectedCompounds || [], [initData]);
    const count = selectedCompounds.length;

    const [engine, setEngine] = useState(MD_ENGINES[0].key);
    const [params, setParams] = useState(() => Object.fromEntries(
        Object.entries(MD_DEFAULTS).map(([k, v]) => [k, String(v)]),
    ));
    const [ligandCharges, setLigandCharges] = useState('am1bcc');
    const [options, setOptions] = useState(MD_OPTION_DEFAULTS);
    const [target, setTarget] = useState(() => defaultTarget(MD_ENGINES[0].jobtype));
    const [chainIds, setChainIds] = useState([]);
    const [ionSpecs, setIonSpecs] = useState([]);
    const [reprotonate, setReprotonate] = useState(false);
    const [runtimeHours, setRuntimeHours] = useState(String(DEFAULT_RUNTIME_HOURS));
    const [jobName, setJobName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitErrors, setSubmitErrors] = useState();

    const engineSpec = MD_ENGINES.find((e) => e.key === engine);
    const targets = mdTargets(engine);
    const features = engineSpec?.features || {};
    // An engine change must not leave a target selected that cannot run it,
    // nor options it cannot run.
    useEffect(() => {
        setTarget((t) => defaultTarget(engineSpec.jobtype, t));
        const f = engineSpec.features || {};
        setOptions((o) => ({
            ...o,
            dynamics: (f.dynamics || ['langevin']).includes(o.dynamics) ? o.dynamics : 'langevin',
            backboneRestraint: f.backboneRestraint ? o.backboneRestraint : '0',
            annealing: f.annealing ? o.annealing : false,
            replicaExchange: f.replicaExchange ? o.replicaExchange : false,
        }));
    }, [engineSpec]);
    const otherCompounds = useMemo(
        () => (workspaceCompounds || []).filter((c) => !selectedCompounds.includes(c)),
        [workspaceCompounds, selectedCompounds]
    );
    // The receptor is the compounds' structure: its chains and ions. Cofactors
    // and other groups are not simulated (the pipeline cannot parameterize
    // them automatically and drops them with a warning).
    const { caseData, mapCase } = useMemo(
        () => (selectedCompounds.length > 0 && App.Ready
            ? (App.getDataParents(selectedCompounds[0]) || {}) : {}),
        [selectedCompounds],
    );
    const proteinChains = useMemo(() => {
        const chains = [
            ...(caseData?.getProteinChains?.() || []),
            ...(caseData?.getPolymers?.() || []),
        ];
        return [...chains].sort((a, b) => String(a.chain).localeCompare(String(b.chain)));
    }, [caseData]);
    const allChainIds = useMemo(() => proteinChains.map((c) => c.chain), [proteinChains]);
    const allIons = useMemo(() => caseData?.getIons?.() || [], [caseData]);
    const hasCofactors = (caseData?.getCofactors?.() || []).length > 0;
    const ionChoices = receptorGroupsOnChains(allIons, chainIds, allChainIds);

    useEffect(() => { setChainIds(allChainIds); }, [mapCase, allChainIds]);
    useEffect(() => {
        setIonSpecs(receptorGroupsOnChains(allIons, chainIds, allChainIds).map((g) => g.resSpec));
    }, [mapCase, chainIds, allIons, allChainIds]);
    const ions = ionChoices.filter((g) => ionSpecs.includes(g.resSpec));

    const problem = mdParamsProblem(params, count)
        || (count > 0 ? mdOptionsProblem(options, params, engine, count) : null);
    const runsPerCompound = options.replicaExchange
        ? Number(options.replicas) : Number(params.copies);
    const totalNs = Number(params.length_ns) * runsPerCompound * count;
    const estimate = !problem ? estimateGpuMinutes(params, count, engine, options) : null;
    const ladder = options.replicaExchange && !problem
        ? remdTemperatures(Number(params.temperature_k), Number(options.maxTemperatureK),
            Number(options.replicas))
        : null;
    const ladderWarning = ladder
        ? remdSpacingWarning(Number(params.temperature_k), Number(options.maxTemperatureK),
            Number(options.replicas))
        : null;

    function setOption(key, value) {
        setOptions((prev) => ({ ...prev, [key]: value }));
    }

    function setSelection(compounds) {
        openMdSidePanel('new_md_tab', { ...initData, selectedCompounds: compounds });
    }

    function setParam(key, value) {
        setParams((prev) => ({ ...prev, [key]: value }));
    }

    // Salt goes on top of neutralizing, so turning neutralizing off clears it.
    function toggleNeutralize(on) {
        setOptions((prev) => ({ ...prev, neutralize: on, saltMolar: on ? prev.saltMolar : '0' }));
    }

    function toggleAnnealing(on) {
        const t = Number(params.temperature_k) || 300;
        const end = Math.round(Number(params.length_ns) * 1000) || 1000;
        setOptions((prev) => ({
            ...prev,
            annealing: on,
            replicaExchange: on ? false : prev.replicaExchange,
            annealingText: prev.annealingText || `0:${t}, ${Math.round(end / 2)}:${t + 50}, ${end}:${t}`,
        }));
    }

    function toggleReplicaExchange(on) {
        setOptions((prev) => ({
            ...prev, replicaExchange: on, annealing: on ? false : prev.annealing,
        }));
        // The replicas take the place of independent runs.
        if (on) setParam('copies', '1');
    }

    function toggleIn(setter) {
        return (key, on) => setter((prev) => (on
            ? [...prev.filter((k) => k !== key), key]
            : prev.filter((k) => k !== key)));
    }

    function disableSubmitMessage() {
        if (problem) return problem;
        if (proteinChains.length > 0 && chainIds.length === 0) return 'Select at least one receptor chain';
        if (!Number.isFinite(Number(runtimeHours)) || Number(runtimeHours) < 0) {
            return 'Runtime cap must be a number of hours (0 for the worker default)';
        }
        return false;
    }

    async function submit() {
        const lines = [
            `${engineSpec.label}: ${count} compound${count === 1 ? '' : 's'} × ${options.replicaExchange ? `${options.replicas} replicas` : `${params.copies} independent run${Number(params.copies) === 1 ? '' : 's'}`} × ${params.length_ns} ns at ${params.temperature_k} K (${+totalNs.toFixed(3)} ns of MD).`,
            `Dynamics: ${describeMdOptions(options, params)}.`,
            `Estimated GPU time: ${describeGpuTime(estimate)} on ${targets.find((t) => t.key === target)?.label || target}.`,
            'Watch it in the Jobs tab.',
        ];
        const ok = await userConfirmation(`Submit ${engineSpec.label} MD?`, lines.join('\n\n'));
        if (!ok) return;
        setIsSubmitting(true);
        setSubmitErrors(undefined);
        try {
            const hours = Number(runtimeHours);
            const { results, error } = await startMdSimulation(selectedCompounds, {
                engine,
                params,
                options,
                chains: proteinChains.length > 0 ? chainIds : undefined,
                ions,
                receptorPrep: reprotonate ? 'full' : 'termini',
                ligandCharges,
                target,
                maxRuntimeMinutes: hours > 0 ? Math.round(hours * 60) : undefined,
                jobName: jobName || undefined,
            });
            if (error) {
                setSubmitErrors([error]);
                return;
            }
            if (!results?.jobid) {
                setSubmitErrors(['No job id returned from the job manager.']);
                return;
            }
            setJobName('');
            setHighlightedJobId(results.jobid);
            openMdSidePanel('md_jobs_tab', { ...initData, selectedCompounds: [] });
        } catch (ex) {
            setSubmitErrors([`There was an error submitting the MD job. ${ex}`]);
        } finally {
            setIsSubmitting(false);
        }
    }

    if (!selected) return null;

    return (
        <>
            <DialogContentWithTooltip
                title={count > 0 ? `Compounds (${count} selected)` : 'Compounds'}
                disablePadding
                style={{ width: '100%' }}
                ContainerProps={{ sx: { overflow: 'unset !important' } }}
                collapsible
                defaultExpand
            >
                {count > 0 ? (
                    <Box component="ul" sx={{ listStyle: 'none', margin: '0.25rem 0.5rem 0', padding: 0 }}>
                        {selectedCompounds.map((c) => (
                            <Box
                                component="li"
                                key={c.resSpec}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.1rem 0',
                                }}
                            >
                                <span style={{ flex: 1, overflowWrap: 'anywhere' }}>{ligandLabel(c)}</span>
                                <IconButton
                                    size="small"
                                    aria-label={`Remove ${c.resSpec}`}
                                    title="Remove from the job"
                                    onClick={() => setSelection(
                                        selectedCompounds.filter((x) => x !== c),
                                    )}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ margin: '0.5rem' }}>
                        {workspaceCompounds?.length > 0
                            ? 'Select docked compounds in the workspace, or add them here.'
                            : 'Load or dock compounds to simulate them in their protein.'}
                    </Box>
                )}
                {otherCompounds.length > 0 && count < MD_LIMITS.maxCompounds && (
                    <Select
                        id="md-add-compound"
                        label="Add a compound"
                        value=""
                        onChange={(e) => {
                            const c = otherCompounds.find((x) => x.resSpec === e.target.value);
                            if (c) setSelection([...selectedCompounds, c]);
                        }}
                        sx={{ width: '100%', marginTop: '0.75rem' }}
                    >
                        <option value="">Choose a workspace compound…</option>
                        {otherCompounds.map((c) => (
                            <option key={c.resSpec} value={c.resSpec}>{ligandLabel(c)}</option>
                        ))}
                    </Select>
                )}
                <Box sx={{ margin: '0.5rem', fontStyle: 'italic' }}>
                    {`Each compound is simulated in its current pose, in its own water box, up to ${MD_LIMITS.maxCompounds} per job.`}
                </Box>
            </DialogContentWithTooltip>

            {count > 0 && (
                <DialogContentWithTooltip
                    title="Receptor"
                    ComponentProps={{ sx: { width: '100%' } }}
                    ContainerProps={{ sx: { overflow: 'unset !important' } }}
                    collapsible
                    defaultExpand
                >
                    <Box sx={{
                        display: 'flex', flexWrap: 'wrap', gap: '1rem 2rem', marginTop: '0.5rem',
                    }}
                    >
                        {proteinChains.length > 1 && (
                            <CheckboxGroup
                                label="Chains"
                                items={proteinChains}
                                selectedKeys={chainIds}
                                getKey={(c) => c.chain}
                                getName={chainLabel}
                                onToggle={toggleIn(setChainIds)}
                                helperText="Only these chains go into the simulation."
                            />
                        )}
                        {ionChoices.length > 0 && (
                            <CheckboxGroup
                                label="Ions"
                                items={ionChoices}
                                selectedKeys={ionSpecs}
                                getKey={(g) => g.resSpec}
                                getName={(g) => g.displayName || g.resSpec}
                                onToggle={toggleIn(setIonSpecs)}
                                helperText="Structural ions kept in the receptor (nonbonded model)."
                            />
                        )}
                    </Box>
                    {hasCofactors && (
                        <FormHelperText>
                            Cofactors are not simulated yet: they are left out of the system.
                        </FormHelperText>
                    )}
                    <Box sx={{ marginTop: '0.75rem' }}>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    id="md-reprotonate"
                                    size="small"
                                    checked={reprotonate}
                                    onChange={(e) => setReprotonate(e.target.checked)}
                                />
                            )}
                            label="Re-protonate the receptor with PDBFixer (pH 7)"
                        />
                        <FormHelperText>
                            {reprotonate
                                ? 'Every hydrogen is re-placed at pH 7; protonation states follow PDBFixer, not BMaps.'
                                : 'BMaps’ protonation states are kept (histidines, charged residues, thiolates).'}
                        </FormHelperText>
                    </Box>
                </DialogContentWithTooltip>
            )}

            <DialogContentWithTooltip
                title="Simulation"
                ComponentProps={{ sx: { width: '100%' } }}
                ContainerProps={{ sx: { overflow: 'unset !important' } }}
                collapsible
                defaultExpand
            >
                <Select
                    id="md-engine"
                    label="Engine"
                    value={engine}
                    onChange={(e) => setEngine(e.target.value)}
                    helperText={engineSpec?.description}
                    sx={{ width: '100%', marginTop: '0.5rem' }}
                >
                    {MD_ENGINES.map((e) => (
                        <option key={e.key} value={e.key}>{e.label}</option>
                    ))}
                </Select>
                <Box sx={{
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(12rem, 1fr))', gap: '0 1rem', marginTop: '0.5rem',
                }}
                >
                    {PARAM_FIELDS.map(({ key, helper }) => (
                        <BasicTextField
                            key={key}
                            id={`md-${key}`}
                            labelText={MD_LIMITS.params[key].label}
                            value={params[key]}
                            onChange={(e) => setParam(key, e.target.value)}
                            required={false}
                            disabled={key === 'copies' && options.replicaExchange}
                            helperText={key === 'copies' && options.replicaExchange
                                ? 'The replicas take the place of independent runs'
                                : helper}
                            sx={{ width: '100%' }}
                        />
                    ))}
                </Box>
                <Select
                    id="md-dynamics"
                    label="Dynamics"
                    value={options.dynamics}
                    onChange={(e) => setOption('dynamics', e.target.value)}
                    helperText={options.dynamics === 'nose_hoover'
                        ? 'Newtonian dynamics with a deterministic Nosé–Hoover thermostat'
                        : 'Langevin dynamics: friction and random kicks hold the temperature (collision rate 1/ps)'}
                    sx={{ width: '100%', marginTop: '1rem' }}
                >
                    {MD_DYNAMICS.map((d) => {
                        const ok = (features.dynamics || ['langevin']).includes(d.key);
                        return (
                            <option key={d.key} value={d.key} disabled={!ok}>
                                {ok ? d.label : `${d.label}: not available with ${engineSpec.label}`}
                            </option>
                        );
                    })}
                </Select>
                <Box sx={{ marginTop: '1rem' }}>
                    <BasicTextField
                        id="md-backbone-restraint"
                        labelText="Backbone restraint (kcal/mol/Å²)"
                        value={options.backboneRestraint}
                        onChange={(e) => setOption('backboneRestraint', e.target.value)}
                        required={false}
                        disabled={!features.backboneRestraint}
                        helperText="Holds the protein backbone (N, CA, C, O) near its starting positions, E = k·d²; 0 = off. 1–5 allows local motion; 10 or more holds it nearly fixed."
                        sx={{ width: '100%' }}
                    />
                </Box>
                <Box sx={{ marginTop: '1rem' }}>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                id="md-neutralize"
                                size="small"
                                checked={options.neutralize !== false}
                                onChange={(e) => toggleNeutralize(e.target.checked)}
                            />
                        )}
                        label="Neutralize with Na⁺/Cl⁻"
                    />
                    {options.neutralize === false ? (
                        <FormHelperText error id="md-neutralize-warning">
                            No counter-ions are added, so a charged system keeps its net
                            charge. Periodic electrostatics (PME) then assume a uniform
                            neutralizing background, and the results depend on the box size.
                            Use this only on purpose, for a system whose net charge you want
                            left as built.
                        </FormHelperText>
                    ) : (
                        <FormHelperText>
                            Adds Na⁺ or Cl⁻ until the net charge of protein and ligand is zero.
                        </FormHelperText>
                    )}
                </Box>
                <Box sx={{ marginTop: '0.75rem' }}>
                    <BasicTextField
                        id="md-salt-molar"
                        labelText="Salt concentration (M NaCl)"
                        value={options.neutralize === false ? '0' : options.saltMolar}
                        onChange={(e) => setOption('saltMolar', e.target.value)}
                        required={false}
                        disabled={options.neutralize === false}
                        helperText="NaCl added beyond the Na⁺/Cl⁻ ions that neutralize the system; 0 = neutralize only. 0.15 is physiological."
                        sx={{ width: '100%' }}
                    />
                </Box>
                <Box sx={{ marginTop: '0.75rem' }}>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                id="md-annealing"
                                size="small"
                                checked={!!options.annealing}
                                disabled={!features.annealing}
                                onChange={(e) => toggleAnnealing(e.target.checked)}
                            />
                        )}
                        label="Temperature annealing"
                    />
                    {!features.annealing && (
                        <FormHelperText>{`Not available with ${engineSpec.label}.`}</FormHelperText>
                    )}
                    {options.annealing && (
                        <BasicTextField
                            id="md-annealing-schedule"
                            labelText="Schedule (ps:K)"
                            value={options.annealingText}
                            onChange={(e) => setOption('annealingText', e.target.value)}
                            required={false}
                            helperText="time:temperature points, starting at 0 at the run's temperature; linear in between, and the last temperature holds to the end"
                            sx={{ width: '100%' }}
                        />
                    )}
                </Box>
                <Box sx={{ marginTop: '0.5rem' }}>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                id="md-replica-exchange"
                                size="small"
                                checked={!!options.replicaExchange}
                                disabled={!features.replicaExchange}
                                onChange={(e) => toggleReplicaExchange(e.target.checked)}
                            />
                        )}
                        label="Replica exchange (temperature)"
                    />
                    {!features.replicaExchange && (
                        <FormHelperText>{`Not available with ${engineSpec.label}.`}</FormHelperText>
                    )}
                    {options.replicaExchange && (
                        <Box sx={{
                            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(10rem, 1fr))', gap: '0 1rem',
                        }}
                        >
                            <BasicTextField
                                id="md-replicas"
                                labelText="Replicas"
                                value={options.replicas}
                                onChange={(e) => setOption('replicas', e.target.value)}
                                required={false}
                                helperText="2–16, all on one GPU"
                                sx={{ width: '100%' }}
                            />
                            <BasicTextField
                                id="md-max-temperature"
                                labelText="Top temperature (K)"
                                value={options.maxTemperatureK}
                                onChange={(e) => setOption('maxTemperatureK', e.target.value)}
                                required={false}
                                helperText="The hottest replica"
                                sx={{ width: '100%' }}
                            />
                            <BasicTextField
                                id="md-exchange-ps"
                                labelText="Exchange every (ps)"
                                value={options.exchangePs}
                                onChange={(e) => setOption('exchangePs', e.target.value)}
                                required={false}
                                helperText="Whole ps"
                                sx={{ width: '100%' }}
                            />
                        </Box>
                    )}
                    {ladderWarning && (
                        <FormHelperText error id="md-remd-warning">{ladderWarning}</FormHelperText>
                    )}
                    {ladder && (
                        <FormHelperText>
                            {`Temperatures: ${ladder.join(', ')} K. Results are those of the ${ladder[0]} K replica. In water, neighbors exchange only when about 0.5% apart: for a typical complex (~60,000 atoms), 16 replicas span about 300–321 K; wider ladders swap almost never (the job reports the acceptance).`}
                        </FormHelperText>
                    )}
                </Box>
                <Select
                    id="md-ligand-charges"
                    label="Ligand charges"
                    value={ligandCharges}
                    onChange={(e) => setLigandCharges(e.target.value)}
                    helperText={ligandCharges === 'submitted'
                        ? 'The partial charges BMaps computes for each compound (AM1-BCC, or OpenFF NAGL with the OpenFF force-field preference); requested from the server at submit if missing.'
                        : 'AM1-BCC charges computed in the job (about a minute per compound).'}
                    sx={{ width: '100%', marginTop: '1rem' }}
                >
                    {MD_LIGAND_CHARGES.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                </Select>
                <Select
                    id="md-target"
                    label="Compute"
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    sx={{ width: '100%', marginTop: '1rem' }}
                >
                    {targets.map((t) => (
                        <option key={t.key} value={t.key} disabled={t.disabled}>{t.label}</option>
                    ))}
                </Select>
                <Box sx={{ marginTop: '1rem' }}>
                    <BasicTextField
                        id="md-runtime-hours"
                        labelText="Runtime cap (hours)"
                        value={runtimeHours}
                        onChange={(e) => setRuntimeHours(e.target.value)}
                        required={false}
                        helperText="May only lower the worker's cap; compounds that finished are still reported"
                        sx={{ width: '100%' }}
                    />
                </Box>
                <Box sx={{ marginTop: '1rem' }}>
                    <BasicTextField
                        id="md-job-name"
                        labelText="Job name"
                        value={jobName}
                        onChange={(e) => setJobName(e.target.value)}
                        required={false}
                        sx={{ width: '100%' }}
                    />
                </Box>
                <Box sx={{ margin: '0.75rem 0 0', fontStyle: 'italic' }}>
                    {problem
                        ? <span style={{ color: 'var(--marketing-orange, #b8650c)' }}>{problem}</span>
                        : `${+totalNs.toFixed(3)} ns of MD in total; estimated GPU time ${describeGpuTime(estimate)}.`}
                </Box>
            </DialogContentWithTooltip>

            <BMapsTooltip title={disableSubmitMessage() || ''}>
                {wrapForDisabledTooltip(
                    <DialogPrimaryButton
                        onClick={submit}
                        disabled={!!disableSubmitMessage() || isSubmitting}
                        sx={{ width: '100%' }}
                    >
                        {isSubmitting ? 'Submitting…' : `Submit ${engineSpec?.label || 'MD'} job`}
                    </DialogPrimaryButton>
                )}
            </BMapsTooltip>
            <span style={{ fontStyle: 'italic' }}>
                MD runs as a job outside your BMaps session. Watch it in the
                {' '}
                <Link /* eslint-disable-line jsx-a11y/anchor-is-valid */
                    component="button"
                    onClick={() => openMdSidePanel('md_jobs_tab')}
                    sx={{ color: 'var(--marketing-blue)' }}
                >
                    Jobs
                </Link>
                {' '}
                tab; pose stability, interactions and plots are in the Results tab.
            </span>
            {submitErrors && <ImportErrors errors={submitErrors} />}
        </>
    );
}
